import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { RewardsReportInterface } from 'src/app/components/analytics/reports/analytics-rewards-report/rewards-report.interface';
import { AllowanceAllocationReportInterface } from 'src/app/components/analytics/reports/analytics-allowance-allocation-report/allowance-allocation-report.interface';
import {
    IBankTransactionInterface,
    ITransactionInterface
} from '../../components/analytics/analytics-peer-to-peer/peer-to-peer-transaction.interface';
import { PaymentReportInterface } from '../../components/analytics/reports/analytics-payment-report/payment-report.interface';
import {
    SpendingReportInterface
} from '../../components/analytics/reports/analytics-spending-report/spending-report.interface';
import { AllocationReportInterface } from 'src/app/components/analytics/reports/analytics-allocation-report/allocation-report.interface';

export interface Transaction {
    transactionId: string;
    transactionDate: string;
    firstName: string;
    lastName: string;
    userId: string;
    amount: number;
    categories: Array<string>;
    teamPaymentProcessing?: boolean;
}

export interface Purchase {
    id: string;
    userId: string;
    purchaseDate: string;
    description: string;
    amount: string;
    purpose: string;
    merchant: string;
    category: string;
    project: string;
    billable: boolean;
    receiptUrl: string;
    state: string;
    transactionId: string;
    info: string;
}

export interface ReimbursePurchaseWithAllowanceRequest {
    info: string;
    isPartial: boolean;
}

@Injectable({
    providedIn: 'root'
})

export class TransactionsService {

    public transactionStatuses = [
        { number: 0, message: 'All' },
        { number: 3, message: 'Reimbursement requested' },
        { number: 1, message: 'Reimbursement denied' },
        { number: 2, message: 'Reimbursed' }
    ];

    public purchaseStatuses = ['All', 'Reimbursement denied', 'Reimbursed', 'Reimbursement requested', 'Reimbursement approved'];

    constructor(protected restangular: ApiClient) { }

    listTransactions(teamId: string, userId: string, page: number, limit: number, searchValue: string, statusFilter: number): Promise<any> {
        let baseUrl = `/v4/teams/${teamId}/`;
        const filters = [];
        filters.push(`&status=${statusFilter}`);
        baseUrl += `users/${userId}/`;
        return this.restangular.all(baseUrl + `plaid/transaction-history?like=${searchValue}&page=${page}&limit=${limit}${filters.join('')}`).getList().toPromise();
    }

    reimburseTransaction(teamId: string, userId: string, transactionId: string, isPartial: boolean = false): Promise<any> {
        return this.restangular.one('v4/teams', teamId).one('users', userId).one('transactions', transactionId).one('teameligible', '1').customPUT({ isPartial }).toPromise();
    }

    denyTransaction(transactionId: string, teamId: string, message: string = ''): Promise<any> {
        return this.restangular.one('v4').one('teams', teamId).one('transactions', transactionId).one('reimburse-deny').customPUT(message && { message }).toPromise();
    }

    listReimbursements(teamId: string, page: number, limit: number, searchValue: string, includeStatus: string, excludeStatus: string): Promise<any> {
        return this.restangular.one(`teams/${teamId}/activity`).get({ like: searchValue, page, limit, includeStatus, excludeStatus }).toPromise();
    }

    getPurchases(teamId: string, userId: string, filter: string): Promise<any> {
        return this.restangular.one('teams', teamId).one('user', userId).one('purchases').getList().toPromise().then((results: any[]) => results.filter((result: any) => (result.state === null ? '' : result.state) === filter));
    }

    getPendingReceiptApprovals(teamId: string, userId = ''): Promise<any> {
        const params = userId && userId !== 'all' ? { userId } : {};
        return this.restangular
            .one('v5')
            .one('teams', teamId)
            .one('purchases')
            .one('pending-approvals')
            .customGET('', params)
            .toPromise();
    }

    getPendingReceiptApprovalCount(teamId: string): Promise<number> {
        return this.restangular
            .one('v5')
            .one('teams', teamId)
            .one('purchases')
            .one('pending-approval-count')
            .customGET()
            .toPromise()
            .then((result: any) => Number(result?.count || 0));
    }

    setPurchaseState(teamId: string, userId: string, purchaseId: string, state: string, info: string): Promise<any> {
        return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).customPUT({ state, info }).toPromise();
    }

    reimbursePurchaseWithAllowance(teamId: string, userId: string, purchaseId: string, data: ReimbursePurchaseWithAllowanceRequest): Promise<any> {
        return this.restangular
            .one('v4')
            .one('teams', teamId)
            .one('users', userId)
            .one('purchases', purchaseId)
            .one('allowance-reimburse')
            .customPUT(data)
            .toPromise();
    }

    updatePurchase(teamId: string, userId: string, purchaseId: string, data: any): Promise<any> {
        return this.restangular.one('teams', teamId).one('user', userId).one('purchases', purchaseId).customPUT(data).toPromise();
    }

    getTeamPeerToPeerTransactions(teamId: string, activityTransactionStatus: string): Promise<ITransactionInterface[]> {
        return this.restangular.one('teams', teamId).one('activity', activityTransactionStatus).getList().toPromise();
    }

    getTeamPaymentReport(teamId: string): Promise<PaymentReportInterface[]> {
        return this.restangular.one('teams', teamId).one('analytics').one('payment-usage').getList().toPromise();
    }

    getAllowanceAllocationReport(teamId: string): Promise<AllowanceAllocationReportInterface[]> {
        return this.restangular.one('teams', teamId).one('analytics').one('allowance-allocation').getList().toPromise();
    }

    getTeamSpendingReport(teamId: string): Promise<SpendingReportInterface[]> {
        return this.restangular.one('teams', teamId).one('analytics').one('reimbursements-by-category').getList().toPromise();
    }

    getRewardsReport(teamId: string): Promise<RewardsReportInterface> {
        return this.restangular.one('teams', teamId).one('analytics').one('rewards').get().toPromise();
    }

    getAllocationReport(teamId: string): Promise<AllocationReportInterface> {
        return this.restangular.one('teams', teamId).one('analytics').one('allocation').get().toPromise();
    }

    getPeerToPeerTeamTransactionHistory(teamId: string): Promise<IBankTransactionInterface[]> {
        return this.restangular.one('teams', teamId).one('transaction-history').one('p2p').get().toPromise();
    }
}
